// Helper functions for document management
// Ensures consistent ID handling across admin and employee views

/**
 * Get all possible ID variations for an employee
 */
export const getEmployeeIdVariations = (employee) => {
    if (!employee) return [];

    const variations = new Set();

    // Add all possible ID fields
    if (employee.id) variations.add(employee.id);
    if (employee.employee_id) variations.add(employee.employee_id);
    if (employee.user_id) variations.add(employee.user_id);
    if (employee.employee_code) variations.add(employee.employee_code);
    if (employee.email) variations.add(employee.email);
    if (employee.work_email) variations.add(employee.work_email);

    // Remove any null/undefined values
    return Array.from(variations).filter(Boolean);
};

/**
 * Save document for an employee (used by admin when sending)
 */
export const saveDocumentForEmployee = (employee, document) => {
    console.log('=== Saving Document for Employee ===');
    console.log('Employee:', employee);
    console.log('Document:', document);

    // Get all possible IDs for this employee
    const employeeIds = getEmployeeIdVariations(employee);
    console.log('Employee ID variations:', employeeIds);

    // Save document with ALL possible keys to ensure it can be found
    let savedCount = 0;
    employeeIds.forEach(id => {
        const key = `employeeDocuments_${id}`;
        const existingDocs = JSON.parse(localStorage.getItem(key) || '[]');

        // Check if document already exists to avoid duplicates
        const exists = existingDocs.some(doc =>
            doc.id === document.id ||
            (doc.generatedAt === document.generatedAt && doc.category === document.category)
        );

        if (!exists) {
            const updatedDocs = [...existingDocs, document];
            localStorage.setItem(key, JSON.stringify(updatedDocs));
            console.log(`✓ Document saved with key: ${key}`);
            savedCount++;
        } else {
            console.log(`- Document already exists with key: ${key}`);
        }
    });

    console.log(`Document saved to ${savedCount} keys`);
    return savedCount > 0;
};

/**
 * Retrieve documents for an employee (used by employee portal)
 */
export const getEmployeeDocuments = (user) => {
    console.log('=== Retrieving Documents for Employee ===');
    console.log('User:', user);

    // Get all possible IDs for this user
    const userIds = getEmployeeIdVariations(user);
    console.log('User ID variations:', userIds);

    // Collect documents from all possible keys
    const allDocuments = [];
    const documentIds = new Set();

    userIds.forEach(id => {
        const key = `employeeDocuments_${id}`;
        const docs = JSON.parse(localStorage.getItem(key) || '[]');

        if (docs.length > 0) {
            console.log(`✓ Found ${docs.length} documents with key: ${key}`);

            // Add documents, avoiding duplicates
            docs.forEach(doc => {
                if (!documentIds.has(doc.id)) {
                    documentIds.add(doc.id);
                    allDocuments.push(doc);
                }
            });
        }
    });

    console.log(`Total unique documents found: ${allDocuments.length}`);

    // Filter for company documents
    const companyDocs = allDocuments.filter(doc =>
        doc.type === 'company' || doc.sentBy || doc.receivedAt
    );

    console.log(`Company documents: ${companyDocs.length}`);

    return {
        all: allDocuments,
        company: companyDocs,
        personal: allDocuments.filter(doc => !companyDocs.includes(doc))
    };
};

/**
 * Synchronize employee IDs across the system
 */
export const synchronizeEmployeeIds = () => {
    console.log('=== Synchronizing Employee IDs ===');

    // Get current user
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (!currentUser.email) {
        console.log('No user logged in');
        return;
    }

    // Find matching employee in hrportal_employees
    const employees = JSON.parse(localStorage.getItem('hrportal_employees') || '[]');
    const matchingEmployee = employees.find(emp =>
        emp.email === currentUser.email ||
        emp.work_email === currentUser.email ||
        emp.loginCredentials?.loginId === currentUser.email
    );

    if (matchingEmployee) {
        // Create a unified user object with all IDs
        const unifiedUser = {
            ...currentUser,
            id: matchingEmployee.id || currentUser.id,
            employee_id: matchingEmployee.employee_id || matchingEmployee.id || currentUser.employee_id,
            user_id: matchingEmployee.user_id || matchingEmployee.id || currentUser.user_id,
            employee_code: matchingEmployee.employee_code || currentUser.employee_code,
            email: matchingEmployee.email || currentUser.email,
            work_email: matchingEmployee.work_email || matchingEmployee.email || currentUser.work_email
        };

        // Update user in localStorage
        localStorage.setItem('user', JSON.stringify(unifiedUser));
        console.log('✓ User synchronized:', unifiedUser);

        // Merge documents from different keys
        mergeDocumentKeys(currentUser, unifiedUser);

        return unifiedUser;
    }

    console.log('No matching employee found for synchronization');
    return currentUser;
};

/**
 * Merge documents from different keys into unified keys
 */
const mergeDocumentKeys = (oldUser, newUser) => {
    console.log('Merging document keys...');

    const oldIds = getEmployeeIdVariations(oldUser);
    const newIds = getEmployeeIdVariations(newUser);

    // Collect all documents
    const allDocuments = [];
    const documentIds = new Set();

    [...oldIds, ...newIds].forEach(id => {
        const key = `employeeDocuments_${id}`;
        const docs = JSON.parse(localStorage.getItem(key) || '[]');

        docs.forEach(doc => {
            if (!documentIds.has(doc.id)) {
                documentIds.add(doc.id);
                allDocuments.push(doc);
            }
        });
    });

    // Save merged documents to all new keys
    if (allDocuments.length > 0) {
        newIds.forEach(id => {
            const key = `employeeDocuments_${id}`;
            localStorage.setItem(key, JSON.stringify(allDocuments));
            console.log(`✓ Saved ${allDocuments.length} documents to ${key}`);
        });
    }
};

/**
 * Debug function to show all document storage
 */
export const debugDocumentStorage = () => {
    console.log('\n=== DOCUMENT STORAGE DEBUG ===');

    const allKeys = Object.keys(localStorage);
    const docKeys = allKeys.filter(key => key.startsWith('employeeDocuments_'));

    console.log(`Found ${docKeys.length} document keys:`);

    docKeys.forEach(key => {
        const docs = JSON.parse(localStorage.getItem(key) || '[]');
        console.log(`\n${key}:`);
        console.log(`  Documents: ${docs.length}`);
        if (docs.length > 0) {
            console.log('  Latest document:', {
                category: docs[docs.length - 1].category,
                templateName: docs[docs.length - 1].templateName,
                receivedAt: docs[docs.length - 1].receivedAt
            });
        }
    });

    // Check current user
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    console.log('\nCurrent User:', currentUser);

    // Check employees
    const employees = JSON.parse(localStorage.getItem('hrportal_employees') || '[]');
    console.log(`\nEmployees in system: ${employees.length}`);
    employees.forEach(emp => {
        console.log(`  - ${emp.first_name} ${emp.last_name}: ID=${emp.id}, email=${emp.email}`);
    });

    console.log('\n=== END DEBUG ===\n');
};

// Make functions globally available for debugging
if (typeof window !== 'undefined') {
    window.documentHelpers = {
        saveDocumentForEmployee,
        getEmployeeDocuments,
        synchronizeEmployeeIds,
        debugDocumentStorage
    };
}